import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Image, Video, Music, Mic, Package, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL, API } from "@/lib/api";


export default function MediaDownloadCenter({ eventId, isOwner }) {
  const [downloading, setDownloading] = useState({});
  const [completed, setCompleted] = useState({});

  if (!isOwner) return null;

  const downloadCategory = async (category) => {
    setDownloading(prev => ({ ...prev, [category]: true }));
    setCompleted(prev => ({ ...prev, [category]: false }));

    try {
      const response = await axios.get(
        `${API}/events/${eventId}/media/download/${category}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Check if we got a valid response
      if (response.data.size === 0) {
        toast.error(`Keine ${getCategoryLabel(category)} zum Herunterladen`);
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${category}_${eventId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setCompleted(prev => ({ ...prev, [category]: true }));
      toast.success(`${getCategoryLabel(category)} heruntergeladen!`);
      
      // Reset completed status after 3 seconds
      setTimeout(() => {
        setCompleted(prev => ({ ...prev, [category]: false }));
      }, 3000);
    } catch (error) {
      console.error(`Download ${category} failed:`, error);
      if (error.response?.status === 404) {
        toast.error(`Keine ${getCategoryLabel(category)} vorhanden`);
      } else {
        toast.error(`Fehler beim Herunterladen der ${getCategoryLabel(category)}`);
      }
    } finally {
      setDownloading(prev => ({ ...prev, [category]: false }));
    }
  };

  const downloadAll = async () => {
    setDownloading(prev => ({ ...prev, all: true }));
    
    try {
      const response = await axios.get(
        `${API}/events/${eventId}/media/download/all`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );

      if (response.data.size === 0) {
        toast.error("Keine Medien zum Herunterladen");
        return;
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `alle_medien_${eventId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Alle Medien heruntergeladen!");
    } catch (error) {
      console.error("Download all failed:", error);
      toast.error("Fehler beim Herunterladen");
    } finally {
      setDownloading(prev => ({ ...prev, all: false }));
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      photos: "Bilder",
      videos: "Videos",
      music: "Musik",
      audio: "Audio",
      all: "Alle Medien"
    };
    return labels[category] || category;
  };

  const categories = [
    { key: "photos", label: "Bilder", icon: Image, color: "bg-pink-100 text-pink-600", desc: "Alle Fotos inkl. Spiele" },
    { key: "videos", label: "Videos", icon: Video, color: "bg-purple-100 text-purple-600", desc: "Alle Videos" },
    { key: "music", label: "Musik", icon: Music, color: "bg-blue-100 text-blue-600", desc: "Musikwünsche & Uploads" },
    { key: "audio", label: "Audio", icon: Mic, color: "bg-green-100 text-green-600", desc: "Sprachnachrichten" },
  ];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Download className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">Medien herunterladen</h3>
          <p className="text-sm text-gray-500">Lade alle Event-Medien als ZIP herunter</p>
        </div>
      </div>

      {/* Category Downloads */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {categories.map((cat) => (
          <button
            key={cat.key}
            onClick={() => downloadCategory(cat.key)}
            disabled={downloading[cat.key]}
            className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cat.color}`}>
              {downloading[cat.key] ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : completed[cat.key] ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <cat.icon className="w-5 h-5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900">{cat.label}</p>
              <p className="text-xs text-gray-500 truncate">{cat.desc}</p>
            </div>
            <Download className="w-4 h-4 text-gray-400" />
          </button>
        ))}
      </div>

      {/* Download All */}
      <Button
        onClick={downloadAll}
        disabled={downloading.all}
        className="w-full gap-2"
        size="lg"
      >
        {downloading.all ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Wird heruntergeladen...
          </>
        ) : (
          <>
            <Package className="w-5 h-5" />
            Alle Medien herunterladen
          </>
        )}
      </Button>

      <p className="text-xs text-gray-400 text-center mt-3">
        Enthält alle Bilder (auch aus Spielen wie Selfie Game), Videos, Musik und Audio
      </p>
    </div>
  );
}
